import type { ActivityItem } from '../types';

/**
 * Generic entity-activity adapter.
 *
 * Normalizes any module's per-entity activity rows into the shared
 * `ActivityItem` shape consumed by `<ActivityTimeline />`, so future modules
 * (Projects, Invoices, Targets, …) can render an audit timeline without writing
 * their own component or bespoke mapping — they either use `defaultEntityActivities`
 * (for the common `{ id, action, detail, user_name, created_at }` shape) or pass
 * a custom field map to `toActivityItems`.
 */
export interface EntityActivityFieldMap<T> {
  id: (row: T) => string;
  action: (row: T) => string;
  detail?: (row: T) => string | undefined;
  actor?: (row: T) => string | undefined;
  createdAt: (row: T) => string;
}

export function toActivityItems<T>(rows: T[] | undefined, map: EntityActivityFieldMap<T>): ActivityItem[] {
  return (rows ?? []).map((row) => ({
    id: map.id(row),
    action: map.action(row),
    detail: map.detail?.(row) ?? '',
    actor: map.actor?.(row),
    created_at: map.createdAt(row),
  }));
}

/** Row shape shared by most backend entity-activity collections. */
export interface DefaultEntityActivityRow {
  id: string;
  action: string;
  detail?: string;
  user_name?: string;
  actor?: string;
  created_at: string;
}

/** Convenience adapter for the common entity-activity row shape. */
export function defaultEntityActivities(rows: DefaultEntityActivityRow[] | undefined): ActivityItem[] {
  return toActivityItems(rows, {
    id: (r) => r.id,
    action: (r) => r.action,
    detail: (r) => r.detail,
    actor: (r) => r.actor ?? r.user_name,
    createdAt: (r) => r.created_at,
  });
}
